import React from 'react';

const DashboardCards = ({ stats, styles }) => {
  const getStatusStyles = (status) => {
    // Use variant-specific status colors if available
    const variantStatusColors = styles.statusColors || {};

    const baseStyles = {
      overdue: {
        iconColor: 'text-red-600',
        iconBg: 'bg-red-100',
        textColor: variantStatusColors.overdue || 'text-red-700',
        borderColor: 'border-red-600',
      },
      warning: {
        iconColor: 'text-yellow-600',
        iconBg: 'bg-yellow-100',
        textColor: variantStatusColors.warning || 'text-yellow-700',
        borderColor: 'border-yellow-600',
      },
      compliant: {
        iconColor: 'text-green-600',
        iconBg: 'bg-green-100',
        textColor: variantStatusColors.compliant || 'text-green-700',
        borderColor: 'border-green-600',
      },
    };

    return baseStyles[status] || baseStyles.compliant;
  };

  // For FleetSkipper variant: use statNumberColor directly instead of status-based color
  const useStatNumberColor = styles.id === 'seaready';

  // Helper function to determine message color based on status
  const getMessageColor = (status) => {
    if (!status) return styles.statLabelColor || 'text-gray-600';

    const messageColors = {
      overdue: styles.messageColorOverdue || 'text-red-600',
      warning: styles.messageColorWarning || 'text-amber-600',
      safe: styles.messageColorSafe || 'text-green-600',
      compliant: styles.messageColorSafe || 'text-green-600',
    };

    return messageColors[status] || styles.statLabelColor || 'text-gray-600';
  };

  return (
    <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 ${styles.gridGap || 'gap-6'} mb-8`}>
      {stats.map((stat, index) => {
        const statusStyles = getStatusStyles(stat.status);
        const IconComponent = stat.icon;

        // Determine card border: use cardBorder (ring) if available, otherwise fall back to border
        const cardBorderClass = styles.cardBorder || (styles.borderColor ? `border ${styles.borderColor}` : 'border border-gray-200');

        return (
          <div
            key={index}
            className={`
              ${styles.cardBg || 'bg-white'}
              ${styles.cardPadding || 'p-6'}
              ${styles.cardRounded || styles.rounded}
              ${styles.cardShadow || styles.shadow}
              ${cardBorderClass}
              ${styles.hoverEffect || styles.cardShadowHover || 'hover:shadow-lg'}
              ${styles.transitionNormal || 'transition-all duration-300'}
              cursor-pointer
              ${styles.cardLayout || ''}
            `}
          >
            {/* For FleetSkipper variant: HORIZONTAL layout - icon on RIGHT (Refined style) */}
            {styles.cardLayout ? (
              <div className="flex items-center justify-between">

                {/* LEFT SIDE: Text content (title + message stacked) */}
                <div className="flex-1 flex flex-col gap-1">

                  {/* Title: Small, dark, semibold - Module name */}
                  <p className={`
                    ${styles.cardTitleWeight || 'font-semibold'}
                    ${styles.cardTitleSize || 'text-sm'}
                    ${styles.cardTitleColor || 'text-slate-900'}
                  `}>
                    {stat.title || stat.label}
                  </p>

                  {/* Message: Larger, color-coded - Notification text */}
                  <p className={`
                    ${styles.messageWeight || 'font-medium'}
                    ${styles.messageSize || 'text-base'}
                    ${getMessageColor(stat.status)}
                  `}>
                    {stat.message || stat.value}
                  </p>

                </div>

                {/* RIGHT SIDE: Icon - Always cyan, brand identity */}
                <div className="flex-shrink-0 ml-4">
                  <IconComponent
                    className={`${styles.iconSize} ${styles.iconColor}`}
                    strokeWidth={styles.strokeWidth || 2}
                  />
                </div>

              </div>
            ) : (
              /* For other variants: HORIZONTAL layout */
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className={`${styles.statLabelWeight || 'font-medium'} ${styles.statLabelSize || 'text-sm'} ${styles.statLabelColor || 'text-gray-600'} ${styles.tracking || ''}`}>
                    {stat.label}
                  </p>
                  <p className={`mt-2 ${styles.statNumberWeight || 'font-bold'} ${styles.statNumberSize || 'text-3xl'} ${useStatNumberColor ? styles.statNumberColor : statusStyles.textColor}`}>
                    {stat.value}
                  </p>
                </div>
                <div className={`${styles.iconBg || ''} ${styles.iconPadding || ''} ${styles.rounded}`}>
                  <IconComponent
                    className={`${styles.iconSize} ${styles.iconBg ? statusStyles.iconColor : styles.iconColor}`}
                    strokeWidth={styles.strokeWidth}
                  />
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default DashboardCards;
